import { authHeaders } from "./auth";
import { apiFetch } from "./backend";
import { loadAllTools, toOpenAIFormat, type Tool, type ToolContext } from "./tools";

export type MessageStats = {
  promptTokens: number;
  completionTokens: number;
  tokensPerSecond: number;
  durationMs: number;
  timeToFirstToken: number;
  model: string;
  contextLength?: number | null;
  reasoningDurationMs?: number;
  effort?: "off" | "low" | "medium" | "high";
};

export type ChatFileRef = {
  type: "file";
  id: string;
  name: string;
  mime: string;
  size: number;
  lines: number | null;
  preview: string | null;
  text?: string;
  source?: File;
};

export type AttachedFile =
  | { type: "text"; name: string; content: string }
  | { type: "image"; name: string; content: string; dataUrl: string; fileId?: string; source?: File }
  | { type: "paste"; id: number; name: string; content: string }
  | ChatFileRef;

export const INLINE_TEXT_BYTES = 2 * 1024;

type UploadedFile = Omit<ChatFileRef, "type" | "text" | "source">;

export async function uploadPendingFiles(chatId: string, files: AttachedFile[] | undefined): Promise<AttachedFile[] | undefined> {
  const pending = files?.filter((f) => (f.type === "file" || f.type === "image") && f.source) ?? [];
  if (!files || pending.length === 0) return files;
  const form = new FormData();
  for (const f of pending) form.append("file", (f as { source: File }).source);
  const response = await apiFetch(`/api/chats/${chatId}/files`, { method: "POST", body: form });
  if (!response.ok) throw new Error(`Couldn't upload attachments: ${await response.text()}`);
  const uploaded = (await response.json()) as UploadedFile[];

  const results = new Map(pending.map((f, i) => [f, uploaded[i]]));
  return Promise.all(files.map(async (f) => {
    const saved = results.get(f);
    if (!saved) return f;
    if (f.type === "image") return { ...f, source: undefined, fileId: saved.id };
    const small = saved.lines !== null && saved.size <= INLINE_TEXT_BYTES;
    const text = small ? await (f as ChatFileRef).source!.text() : undefined;
    const { id, name, mime, size, lines, preview } = saved;
    return { type: "file" as const, id, name, mime, size, lines, preview, ...(text !== undefined && { text }) };
  }));
}

export async function downloadChatFile(chatId: string, fileId: string, name: string) {
  const response = await apiFetch(`/api/chats/${chatId}/files/${fileId}`);
  if (!response.ok) throw new Error(`Download failed: ${response.status}`);
  const url = URL.createObjectURL(await response.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export type ToolCallInfo = {
  tool: string;
  args: Record<string, unknown>;
  result: unknown;
};

export type TimelinePart =
  | { kind: "reasoning"; text: string }
  | { kind: "content"; text: string }
  | { kind: "tool"; call: ToolCallInfo };

export type Message = {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  reasoning?: string;
  files?: AttachedFile[];
  toolCalls?: ToolCallInfo[];
  timeline?: TimelinePart[];
  activeToolCall?: string;
  activeToolCallChars?: number;
  promptProgress?: number;
  sentAt?: number;
  stats?: MessageStats;
  error?: string;
};

export type ModelInfo = {
  id: string;
  contextLength: number | null;
};

export function generateId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function estimateTokenCount(text: string): number {
  return Math.ceil(text.length / 4);
}

export type Usage = {
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
};

export type StreamChunk = {
  content: string;
  reasoning?: string;
  model?: string;
  toolCalls?: ToolCallInfo[];
  toolCallStart?: { tool: string };
  toolCallProgress?: { tool: string; argumentsLength: number };
  toolCallEnd?: ToolCallInfo;
  promptProgress?: number;
  usage?: Usage;
  snapshot?: {
    content: string;
    reasoning: string;
    toolCalls: ToolCallInfo[];
    timeline: TimelinePart[];
    activeToolCall?: string;
    activeToolCallChars?: number;
    stats?: MessageStats;
  };
  stats?: MessageStats;
  runId?: string;
  assistantMessageId?: string;
};

type ApiMessage = {
  role: string;
  content: string | Array<{ type: string; text?: string; image_url?: { url: string } }>;
  tool_calls?: Array<{ id: string; type: "function"; function: { name: string; arguments: string } }>;
  tool_call_id?: string;
};

export type RunEvent =
  | { type: "snapshot"; content: string; reasoning: string; toolCalls: ToolCallInfo[]; timeline: TimelinePart[]; activeToolCall?: { tool: string; argsChars: number }; stats?: MessageStats }
  | { type: "content"; delta: string }
  | { type: "reasoning"; delta: string }
  | { type: "tool_call_start"; tool: string }
  | { type: "tool_call_progress"; tool: string; argsChars: number }
  | { type: "tool_call_end"; tool: string; args: Record<string, unknown>; result: unknown }
  | { type: "stats"; stats: MessageStats }
  | { type: "need_client_tool"; callId: string; tool: string; args: Record<string, unknown> }
  | { type: "model"; model: string }
  | { type: "done" }
  | { type: "error"; message: string }
  | { type: "cancelled" };

export type RunInfo = { runId: string; assistantMessageId: string };

export async function startServerRun(opts: {
  chatId: string;
  userMessage: { id: string; content: string; files?: AttachedFile[]; sentAt?: number };
  apiMessages: ApiMessage[];
  model?: string;
  effort?: "off" | "low" | "medium" | "high";
  title?: string;
}): Promise<RunInfo> {
  const all = await loadAllTools();
  const clientTools = all.filter((t) => t.source === "web" || t.source === "tauri").map((t) => ({
    name: t.name,
    description: t.description ?? "",
    schema: t.schema,
  }));
  const response = await apiFetch(`/api/chats/${opts.chatId}/runs`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({
      userMessage: {
        id: opts.userMessage.id,
        content: opts.userMessage.content,
        files: opts.userMessage.files,
        sentAt: opts.userMessage.sentAt,
      },
      apiMessages: opts.apiMessages,
      model: opts.model,
      effort: opts.effort ?? "medium",
      title: opts.title,
      clientTools,
    }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Failed to start run: ${response.status} ${text}`);
  }
  return response.json();
}

export async function getActiveRun(chatId: string): Promise<RunInfo | null> {
  const r = await apiFetch(`/api/chats/${chatId}/runs/active`, { headers: authHeaders() });
  if (!r.ok) return null;
  const body = await r.json();
  if (!body.run) return null;
  return { runId: body.run.id, assistantMessageId: body.run.assistantMessageId };
}

export async function cancelServerRun(runId: string): Promise<void> {
  await apiFetch(`/api/runs/${runId}/cancel`, { method: "POST", headers: authHeaders() });
}

export type LastPromptEntry = {
  body: Record<string, unknown>;
  sentAt: number;
  round: number;
};

export type ChatSearchHit = {
  id: string;
  title: string;
  systemPrompt: string | null;
  folderId: string | null;
  pinned?: boolean;
  createdAt: number;
  updatedAt: number;
  snippet: string | null;
  matchedIn: "title" | "content";
};

export async function continueInNewChat(chatId: string, focus?: string): Promise<{ chatId: string; title: string; summary: string; previousChatId: string }> {
  const r = await apiFetch("/api/tools/call", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ name: "continue_in_new_chat", args: focus ? { focus } : {}, chatId }),
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`Continue failed: ${r.status} ${text.slice(0, 200)}`);
  }
  const body = await r.json();
  if (body?.error) throw new Error(body.error);
  return body;
}

export async function suggestChatTitle(chatId: string): Promise<string> {
  const r = await apiFetch(`/api/conversations/${chatId}/title`, {
    method: "POST",
    headers: authHeaders(),
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok || typeof body.title !== "string") {
    throw new Error(`Couldn't suggest a title: ${body.error ?? `HTTP ${r.status}`}`);
  }
  return body.title;
}

export async function searchChats(query: string): Promise<ChatSearchHit[]> {
  const q = query.trim();
  if (!q) return [];
  const r = await apiFetch(`/api/conversations/search?q=${encodeURIComponent(q)}`, { headers: authHeaders() });
  if (!r.ok) return [];
  return r.json();
}

export async function getLastPrompt(chatId: string): Promise<LastPromptEntry | null> {
  const r = await apiFetch(`/api/chats/${chatId}/last-prompt`, { headers: authHeaders() });
  if (!r.ok) return null;
  const body = await r.json();
  return body.entry ?? null;
}

async function submitToolResult(runId: string, callId: string, result: unknown): Promise<void> {
  let lastError: unknown;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const r = await apiFetch(`/api/runs/${runId}/tool-result`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ callId, result }),
      });
      if (r.ok) return;
      lastError = new Error(`HTTP ${r.status}`);
    } catch (e) {
      lastError = e;
    }
    await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
  }
  throw new Error(`Couldn't send the tool result back: ${lastError instanceof Error ? lastError.message : String(lastError)}`);
}

async function findAndRunClientTool(name: string, args: Record<string, unknown>, ctx: ToolContext): Promise<unknown> {
  const tools = await loadAllTools();
  const tool = tools.find((t) => t.name === name);
  if (!tool) return { error: `Unknown client tool: ${name}` };
  try {
    return await tool.run(args, ctx);
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

const MAX_RECONNECTS = 5;

export async function* consumeRunStream(runId: string, chatId: string, signal?: AbortSignal): AsyncGenerator<StreamChunk> {
  const toolResults = new Map<string, unknown>();
  const accumulatedCalls: ToolCallInfo[] = [];
  let reconnects = 0;

  for (;;) {
    let finished = false;
    try {
      for await (const ev of readRunEvents(runId, signal)) {
        reconnects = 0;
        if (ev.type === "need_client_tool") {
          if (!toolResults.has(ev.callId)) {
            toolResults.set(ev.callId, await findAndRunClientTool(ev.tool, ev.args, { chatId }));
          }
          await submitToolResult(runId, ev.callId, toolResults.get(ev.callId));
          continue;
        }
        if (ev.type === "done" || ev.type === "cancelled") { finished = true; break; }
        if (ev.type === "error") throw new RunFailedError(ev.message);
        if (ev.type === "snapshot") accumulatedCalls.splice(0, accumulatedCalls.length, ...ev.toolCalls);
        yield* runEventToChunks(ev, accumulatedCalls);
      }
    } catch (e) {
      if (e instanceof RunFailedError || signal?.aborted) throw e;
      if (reconnects >= MAX_RECONNECTS) throw e;
      console.warn(`Run stream ${runId} failed, reconnecting`, e);
    }
    if (finished || signal?.aborted) return;
    if (reconnects++ >= MAX_RECONNECTS) throw new Error("Lost the connection to the run.");
    await new Promise((resolve) => setTimeout(resolve, 1000 * reconnects));
  }
}

class RunFailedError extends Error {}

async function* readRunEvents(runId: string, signal?: AbortSignal): AsyncGenerator<RunEvent> {
  const response = await apiFetch(`/api/runs/${runId}/stream`, { headers: authHeaders(), signal });
  if (!response.ok) {
    throw new RunFailedError(`Failed to attach to run stream: ${response.status}`);
  }
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data: ")) continue;
      try { yield JSON.parse(trimmed.slice(6)); } catch {}
    }
  }
}

function runEventToChunks(ev: RunEvent, accumulatedCalls: ToolCallInfo[]): StreamChunk[] {
  switch (ev.type) {
    case "snapshot":
      return [{
        content: "",
        snapshot: {
          content: ev.content,
          reasoning: ev.reasoning,
          toolCalls: ev.toolCalls,
          timeline: ev.timeline ?? [],
          activeToolCall: ev.activeToolCall?.tool,
          activeToolCallChars: ev.activeToolCall?.argsChars,
          stats: ev.stats,
        },
      }];
    case "content":
      return [{ content: ev.delta }];
    case "reasoning":
      return [{ content: "", reasoning: ev.delta }];
    case "model":
      return [{ content: "", model: ev.model }];
    case "tool_call_start":
      return [{ content: "", toolCallStart: { tool: ev.tool } }];
    case "tool_call_progress":
      return [{ content: "", toolCallProgress: { tool: ev.tool, argumentsLength: ev.argsChars } }];
    case "tool_call_end": {
      const info: ToolCallInfo = { tool: ev.tool, args: ev.args, result: ev.result };
      accumulatedCalls.push(info);
      return [{ content: "", toolCallEnd: info }, { content: "", toolCalls: [...accumulatedCalls] }];
    }
    case "stats":
      return [{ content: "", stats: ev.stats }];
    default:
      return [];
  }
}


export async function fetchModels(): Promise<ModelInfo[]> {
  const response = await apiFetch("/api/v1/models", { headers: authHeaders() });
  if (!response.ok) return [];
  const data = await response.json();
  return (data.data ?? []).map((m: any) => ({
    id: m.id,
    contextLength: m.context_length ?? null,
  }));
}
